import { useState } from "react";
import type { ReactNode } from "react";
import { Button, Card } from "react-bootstrap";
import {
  IoCalendarOutline,
  IoCardOutline,
  IoFlagOutline,
  IoPaperPlaneOutline,
  IoRibbonOutline,
  IoSyncOutline,
  IoTimeOutline,
} from "react-icons/io5";
import type { Subscription } from "../../../features/users/usersTypes";
import { findBillingPlanByName } from "../constants/plans";
import {
  formatPlanAmount,
  formatSubscriptionDate,
  formatSubscriptionStatus,
  formatSubscriptionType,
  getNextChargeDate,
  getTrialLabel,
} from "../utils/subscriptionHelpers";
import BookCallModal from "./BookCallModal";

type ActivePlanDetailsProps = {
  subscription: Subscription;
};

type DetailItemProps = {
  icon: ReactNode;
  label: string;
  value: string;
};

function DetailItem({ icon, label, value }: DetailItemProps) {
  return (
    <div className="active-plan-details__detail">
      <span className="active-plan-details__detail-icon" aria-hidden>
        {icon}
      </span>
      <span className="active-plan-details__detail-body">
        <span className="active-plan-details__detail-label">{label}</span>
        <span className="active-plan-details__detail-value">{value}</span>
      </span>
    </div>
  );
}

export default function ActivePlanDetails({ subscription }: ActivePlanDetailsProps) {
  const [showBookCall, setShowBookCall] = useState(false);
  const planDefinition = findBillingPlanByName(subscription.plan_name);
  const trialLabel = getTrialLabel(subscription);
  const billingAmount = formatPlanAmount(subscription.plan_amount);
  const billingCycle = formatSubscriptionType(subscription.subscription_type);

  return (
    <>
      <BookCallModal show={showBookCall} onHide={() => setShowBookCall(false)} />

      <Card className="active-plan-details">
        <Card.Body>
          <div className="active-plan-details__header">
            <div className="active-plan-details__identity">
              <span className="active-plan-details__plan-icon" aria-hidden>
                <IoRibbonOutline />
              </span>
              <div className="active-plan-details__identity-copy">
                <div className="active-plan-details__title-row">
                  <h3 className="active-plan-details__plan-name mb-0">
                    {subscription.plan_name}
                  </h3>
                  <span className="active-plan-details__badge active-plan-details__badge--active">
                    <span className="active-plan-details__badge-dot" aria-hidden />
                    {formatSubscriptionStatus(subscription.status)}
                  </span>
                  {trialLabel ? (
                    <span className="active-plan-details__badge active-plan-details__badge--trial">
                      {trialLabel}
                    </span>
                  ) : null}
                </div>
                {planDefinition?.subtitle ? (
                  <p className="active-plan-details__subtitle mb-0">
                    {planDefinition.subtitle}
                  </p>
                ) : null}
              </div>
            </div>

            {billingAmount !== "—" ? (
              <div className="active-plan-details__price">
                <span className="active-plan-details__price-amount">
                  {billingAmount}
                </span>
                {billingCycle !== "—" ? (
                  <span className="active-plan-details__price-cycle">
                    per {billingCycle.toLowerCase().replace("ly", "")}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="active-plan-details__details-grid">
            <DetailItem
              icon={<IoSyncOutline />}
              label="Billing cycle"
              value={billingCycle}
            />
            <DetailItem
              icon={<IoCalendarOutline />}
              label="Next charge"
              value={getNextChargeDate(subscription)}
            />
            <DetailItem
              icon={<IoFlagOutline />}
              label="Started on"
              value={formatSubscriptionDate(subscription.started_on)}
            />
            <DetailItem
              icon={<IoCardOutline />}
              label="Last charged"
              value={formatSubscriptionDate(subscription.last_charged_on)}
            />
            <DetailItem
              icon={<IoTimeOutline />}
              label="Ends on"
              value={formatSubscriptionDate(subscription.ended_on)}
            />
          </div>

          <div className="active-plan-details__footer">
            <div className="active-plan-details__footer-copy">
              <span>Need to upgrade, downgrade, or change your billing?</span>
            </div>
            <Button
              type="button"
              variant="primary"
              className="active-plan-details__cta"
              onClick={() => setShowBookCall(true)}
            >
              <IoPaperPlaneOutline aria-hidden />
              Send request
            </Button>
          </div>
        </Card.Body>
      </Card>
    </>
  );
}
